import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Legal & Trust Readiness (Phase 1) — a full-screen, non-dismissible gate
// shown when /auth/me reports needs_legal_reacceptance. Every account that
// existed before legal_acceptances was introduced hits this exactly once;
// it clears the moment the member accepts and never reappears unless a
// document version changes again. Sign-out, reading the documents, and
// exporting/deleting data all stay reachable while this is up — see
// App.js's Protected(), which exempts /app/settings/data-membership and
// /app/export from this gate so those pages render normally underneath.
export function LegalReacceptGate() {
  const { logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await client.post("/legal/accept", { docs: ["terms", "privacy"] });
      await refresh();
    } catch (e) {
      toast.error("Couldn't save your acceptance — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-6 text-center" data-testid="legal-reaccept-gate">
      <ShieldCheck size={40} weight="fill" className="text-gold" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-[#F6F5F1]">We've updated our Terms &amp; Privacy Policy</h1>
      <p className="mt-3 text-[14px] text-dim">
        Please review and accept the latest versions to keep using Screening Circle.
      </p>
      <div className="mt-4 flex gap-4 text-[13px]">
        <a href="/terms" target="_blank" rel="noreferrer" data-testid="legal-reaccept-terms-link" className="font-semibold text-gold underline underline-offset-2 hover:opacity-90">
          Read Terms
        </a>
        <a href="/privacy" target="_blank" rel="noreferrer" data-testid="legal-reaccept-privacy-link" className="font-semibold text-gold underline underline-offset-2 hover:opacity-90">
          Read Privacy Policy
        </a>
      </div>
      <Button onClick={accept} disabled={busy} data-testid="legal-reaccept-accept-btn"
        className="mt-6 h-[54px] w-full rounded-[14px] text-[18px] font-extrabold text-[#1A1400] bg-gradient-to-b from-[#F2D06B] to-[#D4AF37] hover:opacity-90 disabled:opacity-40">
        {busy ? "Saving…" : "Accept & Continue"}
      </Button>
      <div className="mt-6 flex flex-col gap-2 text-[13px] text-dim">
        <button type="button" onClick={() => navigate("/app/settings/data-membership")} data-testid="legal-reaccept-export-link"
          className="underline underline-offset-2 hover:text-gold">
          Export or delete my data first
        </button>
        <button type="button" onClick={logout} data-testid="legal-reaccept-signout-btn"
          className="underline underline-offset-2 hover:text-gold">
          Sign out
        </button>
      </div>
    </div>
  );
}

export default LegalReacceptGate;
